import React, { useEffect } from 'react';

import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';

import Inventory2Outlined from '@mui/icons-material/Inventory2Outlined';

import { Loader } from '@omnigenbiodata/ui';

import BatchSearch from '../../../Home/components/BatchSearch';
import InnerLayout from '../../../../../../layout/Inner';
import { useAppDispatch, useAppSelector } from '../../../../../../store';
import {
  findSampleBatchesThunk,
  hasErrorSelector,
  isBusySelector,
  searchTermSelector,
  responseSelector,
  setValues,
} from '../../../../../../store/batchList';
import ROUTES from '../../../../../../core/constants/routes.constants';
import BatchRow from './components/BatchRow';
import BatchTable from './components/BatchTable';
import AlertNoResults from './components/AlertNoResults';
import { BatchListFormValues } from '../../../../../../store/batchList/types';
import { SampleBatchLookup } from '../../../../../../core/api/lab.types';
import { AlertError } from '../../../../../../components';

function ListScene() {
  const searchTerm = useAppSelector(searchTermSelector);
  const isBusy = useAppSelector(isBusySelector);
  const hasError = useAppSelector(hasErrorSelector);
  const batches = useAppSelector(responseSelector);
  const dispatch = useAppDispatch();

  useEffect(() => {
    if (searchTerm) {
      dispatch(findSampleBatchesThunk(searchTerm));
    }
  }, [searchTerm, dispatch]);

  const handleBatchSearch = (values: BatchListFormValues) => {
    dispatch(setValues(values));
  };

  const showLoader = isBusy;
  const showError = hasError && !isBusy;
  const showResults = !isBusy && !hasError && batches && batches.length > 0;
  const showNoResults = !isBusy && !hasError && batches && batches.length === 0;

  return (
    <InnerLayout>
      <Box
        sx={{
          maxWidth: 1180,
          mx: 'auto',
          px: { xs: 2, md: 3 },
          py: { xs: 3, md: 4 },
        }}
      >
        <Typography variant="h4" component="h1" align="center" paragraph sx={{ fontWeight: 700 }}>
          Sample Batch Search
        </Typography>

        <Paper
          elevation={4}
          sx={{
            mb: 3,
            border: 1,
            borderColor: 'divider',
            borderRadius: 2,
            overflow: 'hidden',
          }}
        >
          <Box sx={{ p: { xs: 2.5, md: 3 } }}>
            <BatchSearch onSearch={handleBatchSearch} searchTerm={searchTerm || ''} />
          </Box>
        </Paper>

        {showError && (
          <Box mb={3}>
            <AlertError title="Something went wrong" description="There was a problem returning sample batches." />
          </Box>
        )}

        {showNoResults && (
          <Box mb={3}>
            <AlertNoResults />
          </Box>
        )}

        {showResults && (
          <Paper
            elevation={4}
            sx={{
              border: 1,
              borderColor: 'divider',
              borderRadius: 2,
              overflow: 'hidden',
              mb: 3,
            }}
          >
            <Box
              sx={{
                px: { xs: 2.5, md: 3 },
                pt: { xs: 2.5, md: 3 },
                pb: 2,
                borderBottom: 1,
                borderColor: 'divider',
                bgcolor: 'background.paper',
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                }}
              >
                <Box
                  sx={{
                    width: 40,
                    height: 40,
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    bgcolor: 'primary.lighter',
                    flexShrink: 0,
                  }}
                >
                  <Inventory2Outlined color="primary" />
                </Box>

                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="h5" component="h2" sx={{ fontWeight: 700 }}>
                    Search Results
                  </Typography>

                  <Typography variant="body2" component="p" color="text.secondary" sx={{ mt: 0.5 }}>
                    {batches.length} matching sample batch{batches.length === 1 ? '' : 'es'} found.
                  </Typography>
                </Box>
              </Box>
            </Box>

            <BatchTable>
              {batches.map((batchRow: SampleBatchLookup) => (
                <BatchRow
                  key={batchRow.batchId}
                  batchId={batchRow.batchId}
                  createdTs={batchRow.createdTs}
                  sampleType={batchRow.sampleType}
                  editLink={ROUTES.batchesEdit.replace(':batchid', batchRow.batchId)}
                />
              ))}
            </BatchTable>
          </Paper>
        )}

        <Loader isVisible={showLoader} />
      </Box>
    </InnerLayout>
  );
}

export default ListScene;
